import { useEffect } from 'react'
import { useUserContext } from '@/UserContext'
import { logger } from '@/services/Logger'
import './video-grid.css'
import {
    hasPinnedPresentation,
    PinnedVideoInfo,
    sessionViewModeState,
    useViewMode,
} from '@/pages/SessionPage/internal/shared/useViewMode'
import { PinnedPresentationOverlay } from '@/pages/SessionPage/internal/shared/PinnedPresentationOverlay'
import { VideoGridPagination } from './VideoGridPagination'
import { VideoGridVideo } from './VideoGridVideo'
import { useVideoSources } from './useVideoSources'
import { useVideoGridContainerRef } from './useVideoGridContainerRef'

const log = logger.createChildLogger({ name: 'VideoGrid' })

type VideoGridProps = {
    pinnedVideo?: PinnedVideoInfo
    page?: number
}

export function VideoGrid(props: VideoGridProps) {
    const [userState] = useUserContext()

    const viewMode = useViewMode()

    const [vids, totalVids] = useVideoSources(props.pinnedVideo, props.page)
    useUnpinKeybind()
    const container = useVideoGridContainerRef(vids.length, totalVids, viewMode.sidebarCollapsed, viewMode.fullscreen)

    return (
        <div
            id="video-grid"
            className="relative w-full h-full min-h-0 overflow-y-auto flex flex-wrap justify-center items-center box-border"
            ref={container}
        >
            {vids.map((vid, i) => (
                <VideoGridVideo
                    key={vid.id}
                    vid={vid}
                    userState={userState}
                    viewMode={viewMode}
                    pinnedVideo={props.pinnedVideo}
                    numVideos={totalVids}
                    index={i}
                />
            ))}

            {hasPinnedPresentation(viewMode) && <PinnedPresentationOverlay fullscreen={viewMode.fullscreen} />}
            <VideoGridPagination viewMode={viewMode} numVideos={totalVids} />
        </div>
    )
}

function useUnpinKeybind() {
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape' && sessionViewModeState.hasPin()) {
                sessionViewModeState.setGalleryLayout({
                    name: 'grid',
                    page: sessionViewModeState.currentGalleryPage,
                    reordering: false,
                })
            }
        }
        document.addEventListener('keydown', handleKeyDown)

        return () => {
            document.removeEventListener('keydown', handleKeyDown)
        }
    }, [])
}
